import React, {
    ChangeEvent,
    useState,
    useEffect,
} from 'react';
import {
    OnKeyboardEvent,
    OnMouseDivEvent,
    OnStringSetState,
} from '@pluto/@types';
import {
    frameToSMPTE,
    toFrames,
    TimeFormat,

} from '@accurate-player/accurate-player-core';
import {curry} from 'ramda';
import wrap from 'wraptile';

import TextInput, {TextInputVariant} from '../TextInput/TextInput';

export type OnFrameChange = (frame: number) => void;
export type NumberOrUndefined = number | undefined;

export interface TimecodeInputContainerProps {
    frame: number;
    duration: number;
    frameRateValue: number;
    dropFrame: boolean;
    autoFocus: boolean;
    isSimpleTime?: boolean,
    variant?: TextInputVariant;
    onClick: (event: OnMouseDivEvent) => void;
    onFrameChange: OnFrameChange;
}

export const onKeyPress = (event: OnKeyboardEvent) => {
    if (event.key === 'Enter')
        event.target.blur();
};

export const onChange = curry((setCurrentSMPTE: OnStringSetState, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentSMPTE(event.target.value);
});

export type OnEmitFrameProps = {
    timecode: string,
    initialTimecode: string,
    duration: number,
    frameRateValue: number,
    dropFrame: boolean,
    setTimecode: OnStringSetState,
    onFrameChange: OnFrameChange,
    isSimpleTime?: boolean,
}

export const isFrame = (value: string): boolean => {
    return value.length !== 8 && !Number.isNaN(Number(value));
};

export const validateFrame = (frame: NumberOrUndefined, duration: number): NumberOrUndefined => {
    if (frame === undefined)
        return frame;

    const value = frame < duration ? frame : duration;
    return value;
};

export const onEmitFrame = wrap((props: OnEmitFrameProps) => {
    const {
        timecode,
        initialTimecode,
        duration,
        frameRateValue,
        dropFrame,
        setTimecode,
        onFrameChange,
        isSimpleTime,
    } = props;

    if (timecode === initialTimecode)
        return;

    if(!isFrame(timecode) && isSimpleTime) {
        setTimecode(timecode);
        const frame = toFrames(
            timecode,
            TimeFormat.Timecode,
            frameRateValue,
            dropFrame,
            duration,
        );
        onFrameChange(frame);
        return;
    }

    let frame: NumberOrUndefined;

    if (isFrame(timecode)) {
        frame = Number(timecode);
    } else {
        frame = toFrames(
            timecode,
            TimeFormat.Timecode,
            frameRateValue,
            dropFrame,
            duration,
        );
    }

    const value = validateFrame(frame, duration);

    if (value === undefined) {
        setTimecode(initialTimecode);
    } else {
        const smpte = frameToSMPTE(frame, frameRateValue, dropFrame);
        setTimecode(smpte);
        onFrameChange(value);
    }
});

export const onBlur = wrap((onEmitFrame: () => void) => {
    onEmitFrame();
});

export type onInitialTimecodeProps = {
    frame: number;
    frameRateValue: number;
    dropFrame: boolean;
    setTimecode: OnStringSetState;
    setInitialTimecode: OnStringSetState;
};

export const onInitialTimecode = wrap((props: onInitialTimecodeProps) => {
    const {
        frame,
        frameRateValue,
        dropFrame,
        setTimecode,
        setInitialTimecode,
    } = props;

    const value = frameToSMPTE(frame, frameRateValue, dropFrame);

    setInitialTimecode(value);
    setTimecode(value);
});

export default function TimecodeInputContainer(props: TimecodeInputContainerProps) {
    const {
        frame,
        frameRateValue,
        dropFrame,
        duration,
        autoFocus,
        variant,
        onClick,
        onFrameChange,
        isSimpleTime,
    } = props;

    const [initialTimecode, setInitialTimecode] = useState('');
    const [timecode, setTimecode] = useState('');
    const handleInitialTimecode = onInitialTimecode({
        frame,
        frameRateValue,
        dropFrame,
        setInitialTimecode,
        setTimecode,
    });
    useEffect(handleInitialTimecode, [frame, frameRateValue, dropFrame, setTimecode]);

    const handleEmitFrame = onEmitFrame({
        timecode,
        initialTimecode,
        dropFrame,
        frameRateValue,
        duration,
        setTimecode,
        onFrameChange,
        isSimpleTime,
    });

    const handleChange = onChange(setTimecode);
    const handleBlur = onBlur(handleEmitFrame);

    return (
        <TextInput
            value={timecode}
            onBlur={handleBlur}
            onChange={handleChange}
            onClick={onClick}
            onKeyPress={onKeyPress}
            autoFocus={autoFocus}
            variant={variant}
        />
    );
}
